import React from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import ErrorList from 'src/components/ErrorList/ErrorList'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import TextInputField, {
  TextInputFieldDesigns,
} from 'src/components/TextInputField/TextInputField'
import { useFlyoutDispatch, useFormFields } from 'src/hooks'
import Trns, { translateSingleString } from 'src/localization/Trns'
import { createSetIsAddSecondaryEmailPopupOpen } from 'src/redux/actions/app.actions'
import { addSecondaryEmail } from 'src/redux/actions/data/users.actions'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import {
  selectSecondaryEmailLoadingFromState,
  selectSecondaryEmailTopLevelErrorFromState,
} from 'src/redux/selectors/data/secondaryEmails.selectors'
import {
  stringIsEmpty,
  stringIsValidEmail,
} from 'src/utils/validation/string.validation'
import { css } from 'styled-components'

export type SecondaryEmailFlatFields = {
  email?: string
}

export type SecondaryEmailVerificationFlatFields = {
  verificationCode?: string
}

const AddSecondaryEmailForm: React.FC = () => {
  const locale = useSelector(selectNullSafeLocaleFromState)
  const loading = useSelector(selectSecondaryEmailLoadingFromState)
  const topLevelError = useSelector(selectSecondaryEmailTopLevelErrorFromState)

  const { analytics } = useAnalyticsContext()
  const dispatch = useFlyoutDispatch()

  const {
    fields: secondaryEmailFlatFields,
    fieldsErrors: secondaryEmailFlatFieldsErrors,
    onFieldChange: onSecondaryEmailFlatFieldChange,
    setErrorsAndReturnDidPassValidation: secondaryEmailFlatFieldsSetErrors,
  } = useFormFields<SecondaryEmailFlatFields>(
    {},
    () => ({
      email: [stringIsEmpty(), stringIsValidEmail()],
    }),
    undefined,
  )

  const sendVerificationCode = (): void => {
    if (secondaryEmailFlatFieldsSetErrors() && secondaryEmailFlatFields.email) {
      analytics.track('secondary email verification - verify')
      dispatch(addSecondaryEmail(secondaryEmailFlatFields.email))
    }
  }

  return (
    <>
      <div
        css={css`
          margin-bottom: 40px;
        `}
      >
        <TextInputField
          fieldLabel={<Trns source={'Secondary Email'} />}
          placeholder={translateSingleString(locale, 'Enter email address')}
          design={TextInputFieldDesigns.New}
          onChange={(val: string): void =>
            onSecondaryEmailFlatFieldChange('email', val)
          }
          type={'email'}
          autoComplete={'email'}
          errors={secondaryEmailFlatFieldsErrors['email']}
        />
        {topLevelError && (
          <ErrorList
            errors={[topLevelError]}
            css={css`
              margin-bottom: 8px;
            `}
          />
        )}
      </div>
      <div
        css={css`
          display: flex;
          gap: 20px;
          align-items: center;
        `}
      >
        <Button
          type={ButtonTypes.button}
          design={ButtonDesigns.designCta}
          big={true}
          text={<Trns source={'Send Verification Code'} />}
          onClick={sendVerificationCode}
          loading={loading}
        />
        <HoverableLink
          underline
          ariaLabelSource={'Cancel'}
          onClick={(): void => {
            analytics.track('secondary email verification - cancel')
            dispatch(createSetIsAddSecondaryEmailPopupOpen(false))
          }}
        >
          <Trns source={'Cancel'} />
        </HoverableLink>
      </div>
    </>
  )
}

export default AddSecondaryEmailForm
